import React, { useEffect, useState, useCallback } from "react";
import { Bell, UserPlus, CheckCheck, Package, Film, Megaphone, CalendarClock } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";

const ICONS = { join: UserPlus, sample: Package, video: Film, campaign: Megaphone, reminder: CalendarClock };
const iconFor = (t) => ICONS[t] || Bell;

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "baru saja";
    if (s < 3600) return `${Math.floor(s / 60)} mnt lalu`;
    if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
    return `${Math.floor(s / 86400)} hari lalu`;
  } catch { return ""; }
};

export default function NotificationBell({ member = false }) {
  const base = member ? "/my-notifications" : "/notifications";
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get(base).then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  }, [base]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const markAll = async () => {
    try { await api.post(`${base}/read-all`); setUnread(0); setItems((p) => p.map((n) => ({ ...n, read: true }))); } catch (e) {}
  };

  return (
    <Popover onOpenChange={(o) => { if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative w-11 h-11 rounded-full bg-white border border-border flex items-center justify-center text-showty-navy hover:bg-showty-pink/40 transition-colors" data-testid="notif-bell" aria-label="Notifikasi">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-showty-red text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notif-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 bg-showty-navy text-white">
          <span className="font-display text-lg">Notifikasi</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs flex items-center gap-1 text-white/80 hover:text-white" data-testid="notif-read-all">
              <CheckCheck className="w-3.5 h-3.5" /> Tandai dibaca
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notif-empty">Belum ada notifikasi</p>
          ) : (
            items.map((n) => {
              const Icon = iconFor(n.type);
              return (
              <div key={n.id} className={`flex gap-3 px-4 py-3 border-b border-border ${n.read ? "bg-white" : "bg-showty-pink/20"}`} data-testid={`notif-${n.id}`}>
                <div className="w-8 h-8 rounded-full bg-showty-red/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-showty-red" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-showty-navy leading-snug">{n.title}</p>
                  {n.body && <p className="text-xs text-muted-foreground">{n.body}</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-showty-red mt-1.5 shrink-0" />}
              </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
